import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  Channel,
  ChannelDetail,
  ChannelMember,
  EntityResolveResult,
  Message,
} from '../../shared/models/chat.models';
import { ChatService } from '../../shared/services/chat.service';
import { ChannelAvatarImageDirective } from '../../shared/directives/channel-avatar-image.directive';
import { MessageInputComponent } from '../message-input/message-input.component';
import { MessageItemComponent } from '../message-item/message-item.component';
import { MessageService } from 'primeng/api';
// import { ChatConnectionBannerComponent } from '../chat-connection-banner/chat-connection-banner.component';

@Component({
  selector: 'app-chat-window',
  templateUrl: './chat-window.component.html',
  styleUrls: ['./chat-window.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    MessageItemComponent,
    MessageInputComponent,
    ChannelAvatarImageDirective,
    // ChatConnectionBannerComponent,
  ],
})
export class ChatWindowComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
  @Input() channel: Channel | null = null;
  @Input() showHeader = true;
  @Input() showInlineGroupMembers = false;
  @Input() renderInlineParticipantLink = true;
  @Input() isMinimized = false;
  @Output() threadOpen = new EventEmitter<Message>();
  @ViewChild('messageList') messageListRef!: ElementRef<HTMLElement>;

  @ViewChild('addMemberSentinel')
  set addMemberSentinel(el: ElementRef<HTMLElement> | undefined) {
    if (el) {
      this.observeAddMemberSentinel(el.nativeElement);
    } else {
      this.disconnectAddMemberObserver();
    }
  }

  messages: Message[] = [];
  channelDetail: ChannelDetail | null = null;
  entityInfo: EntityResolveResult | null = null;
  loading = false;
  hasMore = false;
  page = 1;
  typingUsers = new Map<number, string>();
  private typingTimeouts = new Map<number, any>();

  // ─── Add member dialog ────────────────────────────────────────────────────
  showParticipantsDialog = false;
  showAddMemberDialog = false;
  addMemberQuery = '';
  addMemberResults: {
    user_id: number;
    user_name: string;
    email: string;
    role_name: string;
    profile_pic_url?: string | null;
  }[] = [];
  addMemberSearching = false;
  addMemberLoadingMore = false;
  addMemberHasMore = false;
  addMemberAdding = false;
  private readonly addMemberPageSize = 50;
  private addMemberPage = 1;
  private addMemberSearchTerm = '';
  private addMemberFetchSub: Subscription | null = null;
  private addMemberObserver: IntersectionObserver | null = null;
  private addMemberSearch$ = new Subject<string>();

  // ─── Rename ───────────────────────────────────────────────────────────────
  isRenaming = false;
  renameValue = '';
  renaming = false;

  // ─── Close channel ────────────────────────────────────────────────────────
  showCloseConfirm = false;
  closing = false;

  private destroy$ = new Subject<void>();
  private previousChannelId: number | null = null;
  private needsScrollToBottom = false;
  private lastMarkedReadId: number | null = null;

  constructor(
    private chatService: ChatService,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    // Debounced member search
    this.addMemberSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        this.loadAddMemberUsers(query?.trim() || '');
      });

    this.chatService.wsEvents$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event.type === 'message_ack') {
        if (!event.message) {
          console.warn('[Chat] message_ack received with null message', event);
          return;
        }

        this.messages = this.messages.map((m) =>
          m?.client_id === event.client_id
            ? { ...event.message, delivery_status: 'sent' as const }
            : m
        );
        
        this.chatService.notifyChannelActivity(
          event.message.channel_id,
          event.message.message_text,
          event.message.created_at
        );

        this.cdr.markForCheck();
        return;
      }
      if (event.type === 'mark_read_ack') {
        if (event.channel_id === this.channel?.id) {
          console.log('Read acknowledged:', event.last_read_message_id);
        }
      }

      if (event.type === 'read_update') {
        if (event.channel_id === this.channel?.id) {
          console.log(
            `User ${event.user_id} read up to ${event.last_read_message_id}`
          );
        }
      }

      if (event.type === 'new_message' && event.channel_id === this.channel?.id) {
        const incoming = event.message;
        if (!incoming) {
          console.warn('[Chat] new_message received with null message', event);
          return;
        }
        // Reconcile optimistic send (same client_id) — avoid duplicate bubbles
        if (incoming.client_id) {
          const optIdx = this.messages.findIndex((m) => m?.client_id === incoming.client_id);
          if (optIdx >= 0) {
            this.messages = this.messages.map((m, i) =>
              i === optIdx ? { ...incoming, delivery_status: 'sent' as const } : m
            );
            this.needsScrollToBottom = true;
            this.cdr.markForCheck();
            return;
          }
        }
        const alreadyPresent = this.messages.some((m) => m?.id === incoming.id);
        if (!alreadyPresent) {
          // Fallback if backend omits client_id: replace oldest pending own optimistic bubble
          const pendingIdx = this.messages.findIndex((m) =>
            m != null &&
            m.id < 0 &&
            m.sender_id === incoming.sender_id &&
            m.message_text === incoming.message_text
          );
          if (pendingIdx >= 0) {
            this.messages = this.messages.map((m, i) => (i === pendingIdx ? incoming : m));
          } else {
            this.messages = [...this.messages, incoming];
          }
          this.needsScrollToBottom = true;
          this.cdr.markForCheck();
        }
        if (!this.isMinimized) {
          this.markLatestMessageRead();
        }
      }
      if (event.type === 'reaction_update' && event.channel_id === this.channel?.id) {
        this.messages = this.messages.map((m) =>
          m.id === event.message_id ? { ...m, reactions: event.reactions } : m
        );
        this.cdr.markForCheck();
      }
      if (event.type === 'typing' && event.channel_id === this.channel?.id) {
        this.handleTyping(event);
      }
    });

    this.chatService.reconnected$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.syncGapAfterReconnect();
    });
  }

  private syncGapAfterReconnect(): void {
    if (!this.channel || this.messages.length === 0) return;

    const lastKnownId = this.messages.reduce(
      (max, m) => (m.id > 0 ? Math.max(max, m.id) : max),
      0
    );
    if (lastKnownId <= 0) return;

    this.chatService.syncChannelGap(this.channel.id, lastKnownId).subscribe({
      next: (incoming) => {
        if (incoming.length === 0) return;

        const existingIds = new Set(
          this.messages.filter((m) => m.id > 0).map((m) => m.id)
        );
        const novel = incoming.filter((m) => !existingIds.has(m.id));
        if (novel.length === 0) return;

        this.messages = [...this.messages, ...novel];
        this.needsScrollToBottom = true;
        this.markLatestMessageRead();
        this.cdr.markForCheck();
      },
    });
  }

  private restoreQueuedMessages(): void {
    if (!this.channel) return;

    const queued = this.chatService.getQueuedMessagesForChannel(this.channel.id);
    if (queued.length === 0) return;

    const existingClientIds = new Set(
      this.messages.map((m) => m.client_id).filter(Boolean) as string[]
    );
    const currentUserId = this.getCurrentUserId();

    for (const item of queued) {
      if (existingClientIds.has(item.client_id)) continue;

      this.messages = [
        ...this.messages,
        {
          id: -Date.now() - this.messages.length,
          channel_id: item.channel_id,
          sender_id: currentUserId,
          sender_name: this.getCurrentUserName(),
          message_text: item.payload.message_text,
          message_type: item.payload.message_type ?? 'text',
          parent_message_id: item.payload.parent_message_id ?? null,
          thread_count: 0,
          attachment_url: item.payload.attachment_url ?? null,
          attachment_type: item.payload.attachment_type ?? null,
          attachment_name: item.payload.attachment_name ?? null,
          mentions: item.payload.mentions ?? null,
          reactions: {},
          is_edited: false,
          is_deleted: false,
          created_at: item.queued_at,
          client_id: item.client_id,
          delivery_status: 'queued',
        },
      ];
    }
    this.cdr.markForCheck();
  }

  markCurrentChannelRead(): void {
    this.markLatestMessageRead();
  }

  private handleTyping(event: any): void {
    const currentUserId = this.getCurrentUserId();

    // Do not show the current user's own typing state.
    if (event.user_id === currentUserId) return;

    if (event.is_typing) {
      this.typingUsers.set(event.user_id, event.display_name);

      clearTimeout(this.typingTimeouts.get(event.user_id));

      const timeout = setTimeout(() => {
        this.typingUsers.delete(event.user_id);
        this.typingTimeouts.delete(event.user_id);
        this.cdr.markForCheck();
      }, 3000);

      this.typingTimeouts.set(event.user_id, timeout);
    } else {
      this.typingUsers.delete(event.user_id);
      clearTimeout(this.typingTimeouts.get(event.user_id));
      this.typingTimeouts.delete(event.user_id);
    }

    this.cdr.markForCheck();
  }

  get typingMembers() {
    return Array.from(this.typingUsers.entries()).map(([userId, displayName]) => ({
      userId,
      displayName,
      member: this.members.find((member) => member.user_id === userId),
    }));
  }

  private resetWindow(): void {
  this.messages = [];
  this.channelDetail = null;
  this.entityInfo = null;
  this.page = 1;
  this.hasMore = false;

  this.typingTimeouts.forEach((timeout) => clearTimeout(timeout));
  this.typingTimeouts.clear();
  this.typingUsers.clear();
}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['channel'] && this.channel?.id !== this.previousChannelId) {
      if (this.previousChannelId != null) {
        this.chatService.setActiveChannel(this.previousChannelId, false);
      }
      this.resetWindow();
      if (this.channel) {
        this.previousChannelId = this.channel.id;
        this.chatService.setActiveChannel(this.channel.id, !this.isMinimized);
        this.loadMessages();
        this.loadChannelDetail();
        this.chatService.subscribeToChannel(this.channel.id);
        if (this.channel.linked_entity_type && this.channel.linked_entity_id) {
          this.loadEntityInfo(this.channel.linked_entity_type, this.channel.linked_entity_id);
        }
      } else {
        this.previousChannelId = null;
      }
    }

    if (
      changes['isMinimized'] &&
      !changes['isMinimized'].firstChange &&
      this.channel?.id != null
    ) {
      this.chatService.setActiveChannel(this.channel.id, !this.isMinimized);
    }
  }

  ngAfterViewChecked(): void {
    if (this.needsScrollToBottom) {
      this.needsScrollToBottom = false;
      this.scrollToBottom();
    }
  }

  ngOnDestroy(): void {
    // if (this.previousChannelId) {
    //   this.chatService.unsubscribeFromChannel(this.previousChannelId);
    // }
    this.addMemberFetchSub?.unsubscribe();
    this.disconnectAddMemberObserver();
    if (this.channel?.id != null) {
      this.chatService.setActiveChannel(this.channel.id, false);
    } else if (this.previousChannelId != null) {
      this.chatService.setActiveChannel(this.previousChannelId, false);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadMessages(): void {
    if (!this.channel) return;
    this.loading = true;
    this.chatService
      .getMessages(this.channel.id, this.page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const orderedMessages = res.messages
            .filter((m): m is Message => m != null)
            .reverse();

          if (this.page === 1) {
            this.messages = orderedMessages;
            this.restoreQueuedMessages();
          } else {
            this.messages = [...orderedMessages, ...this.messages];
          }

          this.hasMore = res.has_more;
          this.loading = false;
          this.cdr.markForCheck();
          if (this.page === 1) {
            this.needsScrollToBottom = true;
            this.markLatestMessageRead();
          }
        },
        error: () => {
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private markLatestMessageRead(): void {
    if (!this.channel || this.messages.length === 0 || this.isMinimized) return;

    const latestId = this.messages[this.messages.length - 1]?.id;
    if (!latestId) return;

    // Prevent duplicate mark_read events
    if (this.lastMarkedReadId === latestId) return;

    this.lastMarkedReadId = latestId;

    this.chatService.markChannelRead(this.channel.id, latestId).subscribe();
  }

  onTyping(isTyping: boolean): void {
    if (!this.channel) return;
    this.chatService.sendTyping(this.channel.id, isTyping);
  }

  loadOlderMessages(): void {
    if (!this.hasMore || this.loading) return;
    this.page++;
    this.loadMessages();
  }

  private loadChannelDetail(): void {
    if (!this.channel) return;
    this.chatService
      .getChannelDetail(this.channel.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          this.channelDetail = detail;
          this.cdr.markForCheck();
        },
      });
  }

  private loadEntityInfo(type: string, id: string): void {
    this.chatService
      .resolveEntity(type, id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (info) => {
          this.entityInfo = info;
          this.cdr.markForCheck();
        },
      });
  }

  onMessageSent(message: {
    text: string;
    parentId?: number;
    attachmentUrl?: string;
    attachmentType?: string;
    attachmentName?: string;
    mentions?: number[];
  }): void {
    if (!this.channel) return;

    // Optimistic update: show message immediately; reconcile via client_id on WS new_message
    const clientId = this.chatService.generateClientId();
    const tempId = -Date.now(); // negative so we can identify pending messages
    const currentUserId = this.getCurrentUserId();
    const optimistic: Message = {
      id: tempId,
      channel_id: this.channel.id,
      sender_id: currentUserId,
      sender_name: this.getCurrentUserName(),
      message_text: message.text,
      message_type: 'text',
      parent_message_id: message.parentId ?? null,
      thread_count: 0,
      attachment_url: message.attachmentUrl ?? null,
      attachment_type: message.attachmentType ?? null,
      attachment_name: message.attachmentName ?? null,
      mentions: message.mentions ?? null,
      reactions: {},
      is_edited: false,
      is_deleted: false,
      created_at: new Date().toISOString(),
      client_id: clientId,
      delivery_status: 'sending',
    };
    this.messages = [...this.messages, optimistic];
    this.needsScrollToBottom = true;
    this.cdr.markForCheck();

    this.chatService
      .sendMessage(this.channel.id, {
        message_text: message.text,
        parent_message_id: message.parentId ?? null,
        attachment_url: message.attachmentUrl ?? null,
        attachment_type: message.attachmentType ?? null,
        attachment_name: message.attachmentName ?? null,
        mentions: message.mentions ?? [],
        client_id: clientId,
      })
      .subscribe({
        next: (res) => {
          console.log('message sent', res);
          if (res.via === 'queued') {
            this.updateMessageDeliveryStatus(clientId, 'queued');
          }
        },
        error: () => {
          this.updateMessageDeliveryStatus(clientId, 'failed');
        },
      });
  }

  onRetryMessage(message: Message): void {
    if (!message.client_id) return;

    this.updateMessageDeliveryStatus(message.client_id, 'sending');
    this.chatService.retryOutboundMessage(message.client_id).subscribe({
      next: (res) => {
        if (res.via === 'queued') {
          this.updateMessageDeliveryStatus(message.client_id!, 'queued');
        }
      },
      error: () => {
        this.updateMessageDeliveryStatus(message.client_id!, 'failed');
      },
    });
  }

  private updateMessageDeliveryStatus(
    clientId: string,
    status: Message['delivery_status']
  ): void {
    this.messages = this.messages.map((m) =>
      m.client_id === clientId ? { ...m, delivery_status: status } : m
    );
    this.cdr.markForCheck();
  }

  onOpenThread(message: Message): void {
    this.threadOpen.emit(message);
  }

  onReactToMessage(event: { message: Message; emoji: string }): void {
    if (event.message.id <= 0) return;

    const userId = this.getCurrentUserId();
    const existing = event.message.reactions?.[event.emoji] || [];
    const hasReacted = existing.includes(userId);

    // Optimistic update
    this.messages = this.messages.map((m) => {
      if (m.id !== event.message.id) return m;

      const reactions = { ...m.reactions };

      if (hasReacted) {
        reactions[event.emoji] = existing.filter(id => id !== userId);
        if (reactions[event.emoji].length === 0) {
          delete reactions[event.emoji];
        }
      } else {
        reactions[event.emoji] = [...existing, userId];
      }

      return { ...m, reactions };
    });

    this.cdr.markForCheck();

    this.chatService
      .sendReaction(
        event.message.id,
        event.emoji,
        hasReacted ? 'remove' : 'add'
      )
      .subscribe();
  }

  // ─── Add member ────────────────────────────────────────────────────────────

  openAddMemberDialog(): void {
    this.addMemberFetchSub?.unsubscribe();
    this.addMemberQuery = '';
    this.addMemberResults = [];
    this.addMemberSearchTerm = '';
    this.addMemberPage = 1;
    this.addMemberHasMore = false;
    this.addMemberSearching = false;
    this.addMemberLoadingMore = false;
    this.showAddMemberDialog = true;
    this.cdr.markForCheck();
  }

  onAddMemberInput(): void {
    this.addMemberSearch$.next(this.addMemberQuery);
  }

  loadAddMemberUsers(searchTerm: string, append = false): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.addMemberFetchSub?.unsubscribe();
      this.addMemberSearchTerm = '';
      this.addMemberResults = [];
      this.addMemberHasMore = false;
      this.addMemberSearching = false;
      this.addMemberLoadingMore = false;
      this.cdr.markForCheck();
      return;
    }

    if (append) {
      if (
        !this.addMemberHasMore ||
        this.addMemberSearching ||
        this.addMemberLoadingMore
      ) {
        return;
      }
      this.addMemberLoadingMore = true;
    } else {
      this.addMemberFetchSub?.unsubscribe();
      this.addMemberSearchTerm = searchTerm;
      this.addMemberPage = 1;
      this.addMemberHasMore = false;
      this.addMemberSearching = true;
      this.addMemberLoadingMore = false;
    }

    this.cdr.markForCheck();

    const pageToLoad = append ? this.addMemberPage + 1 : 1;

    this.addMemberFetchSub = this.chatService
      .searchUsers(this.addMemberSearchTerm, pageToLoad, this.addMemberPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          const existingIds = new Set(
            (this.channelDetail?.members ?? []).map((m) => m.user_id)
          );
          const filtered = results.users.filter((r) => !existingIds.has(r.user_id));
          this.addMemberResults = append
            ? this.mergeAddMemberUsers(this.addMemberResults, filtered)
            : filtered;
          this.addMemberPage = pageToLoad;
          this.addMemberHasMore = !results.last;
          this.addMemberSearching = false;
          this.addMemberLoadingMore = false;
          this.cdr.markForCheck();
        },
        error: () => {
          if (!append) {
            this.addMemberResults = [];
            this.addMemberHasMore = false;
          }
          this.addMemberSearching = false;
          this.addMemberLoadingMore = false;
          this.cdr.markForCheck();
        },
      });
  }

  private mergeAddMemberUsers(
    existing: {
      user_id: number;
      user_name: string;
      email: string;
      role_name: string;
      profile_pic_url?: string | null;
    }[],
    incoming: {
      user_id: number;
      user_name: string;
      email: string;
      role_name: string;
      profile_pic_url?: string | null;
    }[]
  ): {
    user_id: number;
    user_name: string;
    email: string;
    role_name: string;
    profile_pic_url?: string | null;
  }[] {
    const seen = new Set(existing.map((u) => u.user_id));
    return [...existing, ...incoming.filter((u) => !seen.has(u.user_id))];
  }

  private observeAddMemberSentinel(element: HTMLElement): void {
    this.disconnectAddMemberObserver();

    const root = element.closest('.am-results');
    this.addMemberObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadAddMemberUsers(this.addMemberSearchTerm, true);
        }
      },
      {
        root: root instanceof Element ? root : null,
        rootMargin: '80px',
        threshold: 0,
      }
    );

    this.addMemberObserver.observe(element);
  }

  private disconnectAddMemberObserver(): void {
    this.addMemberObserver?.disconnect();
    this.addMemberObserver = null;
  }

  addMember(user: { user_id: number; user_name: string }): void {
    if (!this.channel || this.addMemberAdding) return;
    this.addMemberAdding = true;
    this.cdr.markForCheck();
    this.chatService.addMembersToChannel(this.channel.id, [user.user_id]).subscribe({
      next: () => {
        this.addMemberAdding = false;
        this.showAddMemberDialog = false;
        // Refresh member list
        this.loadChannelDetail();
        this.showParticipantsDialog = true;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.detail || 'Failed to add member.',
        });
        this.addMemberAdding = false;
        this.cdr.markForCheck();
      },
    });
  }

  // ─── Rename ───────────────────────────────────────────────────────────────

  startRename(): void {
    this.renameValue = this.channel?.channel_name ?? '';
    this.isRenaming = true;
    this.cdr.markForCheck();
  }

  commitRename(): void {
    const name = this.renameValue.trim();
    if (!name || !this.channel || this.renaming) return;
    this.renaming = true;
    this.cdr.markForCheck();
    this.chatService.renameChannel(this.channel.id, name).subscribe({
      next: () => {
        this.channel = { ...this.channel!, channel_name: name };
        this.isRenaming = false;
        this.renaming = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.renaming = false;
        this.cdr.markForCheck();
      },
    });
  }

  cancelRename(): void {
    this.isRenaming = false;
    this.cdr.markForCheck();
  }

  // ─── Close channel ────────────────────────────────────────────────────────

  confirmClose(): void {
    this.showCloseConfirm = true;
    this.cdr.markForCheck();
  }

  @Output() channelClosed = new EventEmitter<number>();

  doCloseChannel(): void {
    if (!this.channel || this.closing) return;
    this.closing = true;
    this.cdr.markForCheck();
    this.chatService.closeChannel(this.channel.id).subscribe({
      next: () => {
        this.showCloseConfirm = false;
        this.closing = false;
        this.channelClosed.emit(this.channel!.id);
        this.cdr.markForCheck();
      },
      error: () => {
        this.closing = false;
        this.cdr.markForCheck();
      },
    });
  }

  // ─── Channel info ──────────────────────────────────────────────────────────

  get isGroupChannel(): boolean {
    return this.channel?.channel_type === 'group' || this.channel?.channel_type === 'entity_linked';
  }

  getChannelTitle(): string {
    if (this.channel?.channel_name) return this.channel.channel_name;
    if (this.entityInfo?.display_name) return this.entityInfo.display_name;
    if (this.channel?.channel_type === 'direct') {
      return this.directPeer?.user_name ?? 'Direct Message';
    }
    return `Channel #${this.channel?.id}`;
  }

  get headerAvatarInitials(): string {
    if (this.channel?.channel_type === 'direct') {
      return this.getMemberInitials(this.directPeer?.user_name || this.getChannelTitle());
    }
    return this.getMemberInitials(this.getChannelTitle());
  }

  get headerAvatarUrl(): string | null {
    if (this.channel?.profile_pic_url) {
      return this.channel.profile_pic_url;
    }
    if (this.channel?.channel_type === 'direct' && this.directPeer) {
      return this.directPeer.profile_pic_url || this.directPeer.profile_pic || null;
    }
    return null;
  }

  get directPeer() {
    return this.channelDetail?.members.find((m) => m.user_id !== this.getCurrentUserId());
  }

  getChannelSubtitle(): string {
    if (this.entityInfo)
      return `${this.channel?.linked_entity_type} · ${this.channel?.linked_entity_id}`;
    const count = this.channelDetail?.members.length || 0;
    return count > 0 ? `${count} member${count > 1 ? 's' : ''}` : '';
  }

  get shouldShowInlineMembers(): boolean {
    return (
      this.showInlineGroupMembers &&
      this.renderInlineParticipantLink &&
      (
        this.channel?.channel_type === 'group' ||
        this.channel?.channel_type === 'entity_linked'
      )
    );
  }

  get canManageParticipants(): boolean {
    return (
      this.showInlineGroupMembers &&
      (
        this.channel?.channel_type === 'group' ||
        this.channel?.channel_type === 'entity_linked'
      )
    );
  }

  get participantCountLabel(): string {
    const count = this.members.length;
    return `${count} Member${count === 1 ? '' : 's'}`;
  }

  getMemberAvatarUrl(member: ChannelMember): string | null {
    return member?.profile_pic_url || member?.profile_pic || null;
  }

  getMemberInitials(name: string): string {
    const parts = name
      .split(' ')
      .map((part) => part.trim())
      .filter(Boolean)
      .slice(0, 2);
    return parts.map((part) => part.charAt(0).toUpperCase()).join('') || 'U';
  }

  private getCurrentUserId(): number {
    try {
      const data = JSON.parse(localStorage.getItem('optplanner_user') || '{}');
      return data.id || 0;
    } catch {
      return 0;
    }
  }

  private getCurrentUserName(): string {
    try {
      const data = JSON.parse(localStorage.getItem('optplanner_user') || '{}');
      return (
        `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim() || `User ${this.getCurrentUserId()}`
      );
    } catch {
      return '';
    }
  }

  private scrollToBottom(): void {
    if (this.messageListRef?.nativeElement) {
      this.messageListRef.nativeElement.scrollTop = this.messageListRef.nativeElement.scrollHeight;
    }
  }

  get members() {
    return this.channelDetail?.members || [];
  }

  openParticipantsDialog(): void {
    if (!this.canManageParticipants) return;
    this.showParticipantsDialog = true;
    this.cdr.markForCheck();
  }

  openAddMemberFromParticipants(): void {
    this.showParticipantsDialog = false;
    this.openAddMemberDialog();
  }

  trackById(index: number, msg: Message | null): number | string {
    return msg?.client_id ?? msg?.id ?? index;
  }
}
